import type { CalendarDay, CalendarPeriod } from './CalendarTimeline';

const DOW_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTH_LABELS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

function pad(n: number): string {
  return n < 10 ? `0${n}` : `${n}`;
}

function toIso(d: Date): string {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function fmtDayLabel(d: Date): string {
  return `${DOW_LABELS[d.getDay()]} ${MONTH_LABELS[d.getMonth()]} ${d.getDate()}`;
}

function fmtPeriodLabel(start: Date, end: Date): string {
  if (start.getMonth() === end.getMonth() && start.getFullYear() === end.getFullYear()) {
    return `${MONTH_LABELS[start.getMonth()]} ${start.getDate()}–${end.getDate()} ${start.getFullYear()}`;
  }
  return `${MONTH_LABELS[start.getMonth()]} ${start.getDate()} – ${MONTH_LABELS[end.getMonth()]} ${end.getDate()} ${end.getFullYear()}`;
}

export interface AxisConfig {
  /** Period start (ISO). */
  startDate: string;
  /** Exclusive end (ISO). */
  endDate: string;
  /** Number of days per period (e.g. 7 for weekly). Defaults to 7. */
  intervalDays?: number;
}

/**
 * Build a `days`/`periods` array pair matching the same shape the planning grid
 * uses. Reused by non-planning-grid calendar views (e.g. resource reservations)
 * that don't come from the WASM planner but still want to share the visual axis.
 */
export function buildCalendarAxis(config: AxisConfig): {
  days: CalendarDay[];
  periods: CalendarPeriod[];
} {
  const intervalDays = config.intervalDays ?? 7;
  const start = new Date(config.startDate + 'T00:00:00');
  const end = new Date(config.endDate + 'T00:00:00');

  const days: CalendarDay[] = [];
  const periods: CalendarPeriod[] = [];

  let cursor = new Date(start);
  let periodIdx = 0;
  let periodStart = new Date(cursor);
  let daysInPeriod = 0;

  while (cursor < end) {
    days.push({
      date: toIso(cursor),
      label: fmtDayLabel(cursor),
      period_idx: periodIdx,
    });
    daysInPeriod += 1;

    const next = new Date(cursor);
    next.setDate(next.getDate() + 1);

    if (daysInPeriod >= intervalDays || next >= end) {
      const periodEnd = new Date(cursor);
      periods.push({ label: fmtPeriodLabel(periodStart, periodEnd) });
      periodIdx += 1;
      periodStart = new Date(next);
      daysInPeriod = 0;
    }
    cursor = next;
  }

  return { days, periods };
}
